import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QuoteService from '../services/QuoteService';
import ClientService from '../services/ClientService';
import CompanyService from '../services/CompanyService';
import { useMoneyLock } from '../contexts/MoneyContext';
import { fmtDate, todayIso } from '../utils/format';
import { useCompanyLogo } from '../components/common/companyLogo';
import { Edit, ItemsTable, Letterhead, Sheet } from '../components/print/DocumentSheet';

/** A4 quote to print or save as PDF (browser print) and send to the client. */
export default function QuotePrintPage() {
    const { id } = useParams();
    const { t } = useTranslation();
    const logo = useCompanyLogo();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const money = useMoneyLock(`quote:${id}`);

    useEffect(() => {
        if (!money.visible) return;
        Promise.all([QuoteService.get(id), CompanyService.get()])
            .then(([quote, company]) => ClientService.get(quote.client.id).then(client => setData({ quote, company, client })))
            .catch(() => setError(true));
    }, [id, money.visible]);

    const toolbar = (
        <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', maxWidth: '210mm', mx: 'auto', mb: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/quotes/${id}`)} sx={{ color: '#64748B' }}>{t('common.back')}</Button>
            <Typography sx={{ flex: 1, fontSize: 13, color: '#64748B', minWidth: 200 }}>{data ? t('print.editHint') : ''}</Typography>
            <Button variant="contained" startIcon={<PrintOutlinedIcon />} disabled={!data || !money.visible} onClick={() => window.print()}>{t('print.print')}</Button>
        </Box>
    );

    if (!money.visible) {
        return (
            <Box sx={{ p: 3 }}>
                {toolbar}
                <Box sx={{ textAlign: 'center', mt: 8 }}>
                    <LockOutlinedIcon sx={{ color: '#CBD5E1', fontSize: 40 }} />
                    <Typography sx={{ color: '#64748B', mt: 1, mb: 2 }}>{t('money.printLocked')}</Typography>
                    <Button variant="contained" onClick={money.show} disableElevation>{t('money.show')}</Button>
                </Box>
            </Box>
        );
    }

    if (error || !data) {
        return (
            <Box sx={{ p: 3 }}>
                {toolbar}
                <Box sx={{ textAlign: 'center', mt: 8 }}>
                    {error ? <Typography sx={{ color: '#64748B' }}>{t('quotes.notFound')}</Typography> : <CircularProgress />}
                </Box>
            </Box>
        );
    }

    const { quote, company, client } = data;
    // With several kinds of work on one quote, each position shows its category.
    const multiCategory = quote.categories.length > 1;
    const location = [
        quote.municipality && `${t('jobs.fields.municipality')}: ${quote.municipality}`,
        quote.cadastralZone && `${t('jobs.fields.cadastralZone')}: ${quote.cadastralZone}`,
        quote.parcelNumber && `${t('jobs.fields.parcelNumber')}: ${quote.parcelNumber}`,
    ].filter(Boolean).join(' · ');
    const clientAddress = [client.address, client.city].filter(Boolean).join(', ');
    const clientContact = [client.phone, client.email].filter(Boolean).join(' · ');

    return (
        <Sheet toolbar={toolbar}>
            <Letterhead company={company} logo={logo} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4, flexWrap: 'wrap', mb: 5 }}>
                <Box>
                    <Typography sx={{ fontSize: 11.5, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>{t('quotes.printFor')}</Typography>
                    <Edit block sx={{ fontSize: 15, fontWeight: 700 }}>{client.name}</Edit>
                    {client.idNumber && <Edit block sx={{ fontSize: 12.5, color: '#4B5563' }}>{client.idNumber}</Edit>}
                    {clientAddress && <Edit block sx={{ fontSize: 12.5, color: '#4B5563' }}>{clientAddress}</Edit>}
                    {clientContact && <Edit block sx={{ fontSize: 12.5, color: '#4B5563' }}>{clientContact}</Edit>}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Edit block sx={{ fontSize: 22, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('quotes.printTitle')}</Edit>
                    <Edit block sx={{ fontSize: 13, color: '#4B5563' }}>{`${t('quotes.printNo')}: ${quote.number}`}</Edit>
                    <Edit block sx={{ fontSize: 13, color: '#4B5563' }}>{`${t('quotes.printDate')}: ${fmtDate(quote.sentAt || todayIso())}`}</Edit>
                </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
                <Edit block sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>{quote.title}</Edit>
                {location && <Edit block sx={{ fontSize: 13, color: '#374151', mb: 0.5 }}>{location}</Edit>}
                {quote.description && <Edit block sx={{ fontSize: 13, lineHeight: 1.6, color: '#374151' }}>{quote.description}</Edit>}
            </Box>

            <ItemsTable doc={{ ...quote, net: quote.price }} showCategory={multiCategory} />

            <Edit block sx={{ fontSize: 13, lineHeight: 1.7, color: '#374151', mb: 8 }}>{t('quotes.printNote')}</Edit>

            <Box sx={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[t('quotes.printSignCompany'), t('quotes.printSignClient')].map(label => (
                    <Box key={label} sx={{ flex: 1, minWidth: 200 }}>
                        <Box sx={{ borderBottom: '1px solid #111827', height: 56 }} />
                        <Typography sx={{ fontSize: 12, color: '#4B5563', mt: 0.75, textAlign: 'center' }}>{label}</Typography>
                    </Box>
                ))}
            </Box>
        </Sheet>
    );
}
